import React from 'react';
import { Divider, Row, Col } from 'antd';
import { isNil } from 'lodash';

import {
  BreakInServiceEnum,
  CustomerAdditionalInfoResponseModel,
} from 'api/adminApiClient';
import BasePageCard from 'components/wrappers/BasePageCard';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import { downloadFile } from 'utils/fileUtils';
import {
  getCommissionTypeApprovalLabel,
  getContractRenewalTermLabel,
  getContractedMBPServiceLinesLabel,
  getCustomerAgreementTypeLabel,
  getCustomerBreakInServiceLabel,
  getCustomerCostingMethodTypeLabel,
  getCustomerInvoicingCycleLabel,
  getCustomerPaymentTermsLabel,
  getCustomerPayrollCycleLabel,
  getCustomerPreScreeningBilledLabel,
  getIndemnificationTypeLabel,
  getPaymentMethodTypeLabel,
} from 'utils/clientUtils';
import { areObjectValuesPresent } from 'utils/objectUtils';
import {
  ADMIN_ONLY_KEYS,
  areMemberVisibleObjectValuesPresent,
} from 'utils/additionalInfoVisibilityUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import {
  getCustomerCertificateOfInsuranceCreatedFile,
  getCustomerOnboardingDocumentationFile,
  getCustomerPurchaseOrderFile,
  getSOWTemplateFile,
} from 'redux/reducers/clientSlice';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getWebsiteLink, getWebsiteName } from 'utils/urlUtils';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
type Props = {
  clientDetails: CustomerAdditionalInfoResponseModel;
};

export default function ClientAdditionalInfoSection(props: Props) {
  const { clientDetails } = props;
  const isAdminRole = useAppSelector(isAdminSelector);
  const {
    customerKey,
    customerId,
    contractInformation,
    financialInformation,
    assignmentInformation,
  } = clientDetails;
  const dispatch = useAppDispatch();

  const checkSectionVisible = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    obj?: { [key: string]: any },
    adminOnlyKeys?: readonly string[],
  ) =>
    isAdminRole
      ? areObjectValuesPresent(obj)
      : areMemberVisibleObjectValuesPresent(obj, adminOnlyKeys);

  const isFinancialOverviewVisible = checkSectionVisible(
    financialInformation?.financialOverview,
    ADMIN_ONLY_KEYS.financialOverview,
  );
  const isInsuranceRequirementsVisible = areObjectValuesPresent(
    contractInformation?.insuranceRequirements,
  );
  const isRateOverviewVisible = areObjectValuesPresent(
    financialInformation?.rateOverview,
  );
  const isMspOrVmsOverviewVisible = areObjectValuesPresent(
    financialInformation?.mspOrVmsOverview,
  );
  const isAssignmentRequirementsVisible = checkSectionVisible(
    assignmentInformation?.assignmentRequirements,
    ADMIN_ONLY_KEYS.assignmentRequirements,
  );
  const isSpecialRequirementsVisible = areObjectValuesPresent(
    assignmentInformation?.specialAssignmentRequirements,
  );
  const isContractRequirementsVisible = checkSectionVisible(
    contractInformation?.contractRequirements,
    ADMIN_ONLY_KEYS.contractRequirements,
  );
  const noticeParty = contractInformation?.noticeParty;
  const onboardingDocumentationFileKeys =
    assignmentInformation?.assignmentRequirements?.onboardingDocumentationFileKeys;
  const isNoticePartyVisible =
    !!noticeParty?.attentionParty ||
    !!noticeParty?.email ||
    !!noticeParty?.phone ||
    !!noticeParty?.faxNumber ||
    !!noticeParty?.noticeMethod ||
    areObjectValuesPresent(noticeParty?.location);

  const contractSpecialInstructionsValue =
    contractInformation?.contractRequirements?.contractSpecialInstructions;

  const isAdditionalInfo1Visible =
    isInsuranceRequirementsVisible ||
    isContractRequirementsVisible ||
    isNoticePartyVisible;
  const isAdditionalInfo2Visible =
    isFinancialOverviewVisible ||
    isMspOrVmsOverviewVisible ||
    isRateOverviewVisible;
  const isAssignmentInfoVisible =
    isAssignmentRequirementsVisible || isSpecialRequirementsVisible;

  const handleDownloadCustomerCertificateOfInsuranceFile = async () => {
    const result = await dispatch(
      getCustomerCertificateOfInsuranceCreatedFile({
        customerId: customerId as string,
        isAdminRole,
      }),
    );
    if (getCustomerCertificateOfInsuranceCreatedFile.fulfilled.match(result)) {
      const { downloadUrl, fileName } = result.payload;
      try {
        await downloadFile({ downloadUrl, fileName });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadPurchaseOrderFile = async () => {
    if (!customerId) return;

    const result = await dispatch(getCustomerPurchaseOrderFile(customerId));

    if (getCustomerPurchaseOrderFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadOnboardingDocumentationFile = async (fileKey: string) => {
    if (!customerId) return;

    const result = await dispatch(
      getCustomerOnboardingDocumentationFile({
        customerId,
        fileKey,
        isAdminRole,
      }),
    );

    if (getCustomerOnboardingDocumentationFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadSOWTemplateFile = async () => {
    if (!customerId) return;

    const result = await dispatch(
      getSOWTemplateFile({ customerId, isAdminRole }),
    );

    if (getSOWTemplateFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <Row gutter={[10, 10]}>
      {isAdminRole && !isAdditionalInfo1Visible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="Contract Information"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <div className="content-wrapper">
              <BaseSectionMessage
                type="info"
                title="Info"
                message={
                  <span>
                    Please fill in the customer additional information.
                  </span>
                }
              />
            </div>
          </BasePageCard>
        </Col>
      )}

      {isAdditionalInfo1Visible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="Contract Information"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <>
              {isInsuranceRequirementsVisible && (
                <>
                  <Divider className="no-margin" />

                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      <Col xs={24}>
                        <div className="subtitle">Insurance requirements</div>
                      </Col>

                      {!isNil(
                        contractInformation?.insuranceRequirements
                          ?.requiresCustomCOI,
                      ) && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerRequiresCustomCOI"
                        >
                          <div className="title">Requires custom COI?:</div>
                          <div>
                            {contractInformation?.insuranceRequirements
                              ?.requiresCustomCOI
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.insuranceRequirements
                        ?.submittedTo && (
                        <Col xs={24} md={12} data-id="customerSubmittedTo">
                          <div className="title">Submitted to:</div>
                          <div>
                            {
                              contractInformation?.insuranceRequirements
                                ?.submittedTo
                            }
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.insuranceRequirements
                        ?.certificateOfInsuranceCreatedFileKey && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerDownloadCertificate"
                        >
                          <div className="title">
                            Download certificate of insurance created:
                          </div>
                          <div>
                            <span
                              className="link-imitation"
                              onClick={
                                handleDownloadCustomerCertificateOfInsuranceFile
                              }
                            >
                              {`Customer ${customerKey} certificate of insurance created`}
                            </span>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </>
              )}

              {isContractRequirementsVisible && (
                <>
                  <Divider className="no-margin" />
                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      <Col xs={24}>
                        <div className="subtitle">Contract requirements</div>
                      </Col>

                      {!isNil(
                        contractInformation?.contractRequirements
                          ?.canContractBeAssigned,
                      ) && (
                        <Col xs={24} md={12}>
                          <div className="title">
                            Can the contract be assigned?:
                          </div>
                          <div>
                            {contractInformation?.contractRequirements
                              ?.canContractBeAssigned
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        contractInformation?.contractRequirements
                          ?.useOfNameLogoPermitted,
                      ) && (
                        <Col xs={24} md={12}>
                          <div className="title">
                            Use of name / logo permitted?:
                          </div>
                          <div>
                            {contractInformation?.contractRequirements
                              ?.useOfNameLogoPermitted
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        contractInformation?.contractRequirements
                          ?.customerAgreementType,
                      ) && (
                        <Col xs={24} md={12}>
                          <div className="title">Customer agreement type:</div>
                          <div>
                            {getCustomerAgreementTypeLabel(
                              contractInformation?.contractRequirements
                                ?.customerAgreementType,
                            )}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.contractRequirements
                        ?.governingLawJurisdiction && (
                        <Col xs={24} md={12}>
                          <div className="title">
                            Governing law jurisdiction:
                          </div>
                          <div>
                            {
                              contractInformation?.contractRequirements
                                ?.governingLawJurisdiction
                            }
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.contractRequirements
                        ?.indemnificationType && (
                        <Col xs={24} md={12}>
                          <div className="title">Indemnification type:</div>
                          <div>
                            {getIndemnificationTypeLabel(
                              contractInformation?.contractRequirements
                                ?.indemnificationType,
                            )}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.contractRequirements
                        ?.limitationOfLiability && (
                        <Col xs={24} md={12}>
                          <div className="title">Limitation of liability:</div>
                          <div>
                            {
                              contractInformation?.contractRequirements
                                ?.limitationOfLiability
                            }
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        contractInformation?.contractRequirements
                          ?.rightToInspectOrAudit,
                      ) && (
                        <Col xs={24} md={12}>
                          <div className="title">
                            Right to inspect or audit?:
                          </div>
                          <div>
                            {contractInformation?.contractRequirements
                              ?.rightToInspectOrAudit
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.contractRequirements
                        ?.contractedMBPServiceLines?.length && (
                        <Col xs={24} md={12}>
                          <div className="title">
                            Contracted MBP service line(s):
                          </div>
                          <div>
                            {contractInformation?.contractRequirements?.contractedMBPServiceLines
                              ?.map((line) =>
                                getContractedMBPServiceLinesLabel(line),
                              )
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        contractInformation?.contractRequirements
                          ?.dataPrivacyRequirements,
                      ) && (
                        <Col xs={24} md={12}>
                          <div className="title">
                            Data privacy requirements:
                          </div>
                          <div>
                            {contractInformation?.contractRequirements
                              ?.dataPrivacyRequirements
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.contractRequirements
                        ?.dataPrivacyInstructions && (
                        <Col xs={24} md={12}>
                          <div className="title">
                            Data privacy instructions:
                          </div>
                          <div>
                            {
                              contractInformation?.contractRequirements
                                ?.dataPrivacyInstructions
                            }
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.contractRequirements
                        ?.contractRenewalTerm && (
                        <Col xs={24} md={12}>
                          <div className="title">Contract renewal term:</div>
                          <div>
                            {getContractRenewalTermLabel(
                              contractInformation?.contractRequirements
                                ?.contractRenewalTerm,
                            )}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.contractRequirements
                        ?.contractEffectiveDate && (
                        <Col xs={24} md={12}>
                          <div className="title">Contract effective date:</div>
                          <div>
                            {new Date(
                              contractInformation?.contractRequirements
                                ?.contractEffectiveDate as Date,
                            ).toLocaleDateString()}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.contractRequirements
                        ?.contractExpirationDate && (
                        <Col xs={24} md={12}>
                          <div className="title">
                            Contract expiration date:
                          </div>
                          <div>
                            {new Date(
                              contractInformation?.contractRequirements
                                ?.contractExpirationDate as Date,
                            ).toLocaleDateString()}
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        contractInformation?.contractRequirements
                          ?.noticePeriodInDays,
                      ) && (
                        <Col xs={24} md={12}>
                          <div className="title">Notice period (in days):</div>
                          <div>
                            {
                              contractInformation?.contractRequirements
                                ?.noticePeriodInDays
                            }
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        contractInformation?.contractRequirements?.sowRequired,
                      ) && (
                        <Col xs={24} md={12} data-id="customerSOWRequired">
                          <div className="title">SOW required:</div>
                          <div>
                            {contractInformation?.contractRequirements
                              ?.sowRequired
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.contractRequirements
                        ?.sowSpecialInstructions && (
                        <Col xs={24} md={12} data-id="customerSOWInstructions">
                          <div className="title">SOW special instructions:</div>
                          <div>
                            {
                              contractInformation?.contractRequirements
                                ?.sowSpecialInstructions
                            }
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.contractRequirements
                        ?.sowTemplateFileKey && (
                        <Col xs={24} md={12} data-id="customerSOWTemplate">
                          <div className="title">SOW template:</div>
                          <div>
                            <span
                              className="link-imitation"
                              onClick={handleDownloadSOWTemplateFile}
                            >
                              Customer {customerKey} SOW template
                            </span>
                          </div>
                        </Col>
                      )}

                      {isAdminRole &&
                        !!contractInformation?.contractRequirements
                          ?.declinationReason && (
                          <Col xs={24} md={12}>
                            <div className="title">Declination reason:</div>
                            <div>
                              {
                                contractInformation?.contractRequirements
                                  ?.declinationReason
                              }
                            </div>
                          </Col>
                        )}

                      {!!contractSpecialInstructionsValue && (
                        <Col xs={24} md={12}>
                          <div className="title">
                            Contract special instructions:
                          </div>
                          <div>{contractSpecialInstructionsValue}</div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </>
              )}

              {isNoticePartyVisible && (
                <>
                  <Divider className="no-margin" />
                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      <Col xs={24}>
                        <div className="subtitle">Notice party</div>
                      </Col>

                      {!!contractInformation?.noticeParty?.attentionParty && (
                        <Col xs={24} md={12}>
                          <div className="title">Attention party:</div>
                          <div>
                            {contractInformation?.noticeParty?.attentionParty}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.noticeParty?.location?.address && (
                        <Col xs={24} md={12}>
                          <div className="title">Street:</div>
                          <div>
                            {
                              contractInformation?.noticeParty?.location
                                ?.address
                            }
                          </div>
                        </Col>
                      )}

                      {(!!contractInformation?.noticeParty?.location?.city ||
                        !!contractInformation?.noticeParty?.location
                          ?.internationalCityName) && (
                        <Col xs={24} md={12}>
                          <div className="title">City:</div>
                          <div>
                            {contractInformation?.noticeParty?.location
                              ?.city ||
                              contractInformation?.noticeParty?.location
                                ?.internationalCityName}
                          </div>
                        </Col>
                      )}

                      {(!!contractInformation?.noticeParty?.location?.state ||
                        !!contractInformation?.noticeParty?.location
                          ?.internationalStateName) && (
                        <Col xs={24} md={12}>
                          <div className="title">State / province:</div>
                          <div>
                            {contractInformation?.noticeParty?.location
                              ?.state ||
                              contractInformation?.noticeParty?.location
                                ?.internationalStateName}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.noticeParty?.location
                        ?.country && (
                        <Col xs={24} md={12}>
                          <div className="title">Country:</div>
                          <div>
                            {
                              contractInformation?.noticeParty?.location
                                ?.country
                            }
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.noticeParty?.location
                        ?.zipCode && (
                        <Col xs={24} md={12}>
                          <div className="title">ZIP code:</div>
                          <div>
                            {
                              contractInformation?.noticeParty?.location
                                ?.zipCode
                            }
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.noticeParty?.email && (
                        <Col xs={24} md={12}>
                          <div className="title">Email:</div>
                          <div>{contractInformation?.noticeParty?.email}</div>
                        </Col>
                      )}

                      {!!contractInformation?.noticeParty?.phone && (
                        <Col xs={24} md={12}>
                          <div className="title">Phone:</div>
                          <div>{contractInformation?.noticeParty?.phone}</div>
                        </Col>
                      )}

                      {!!contractInformation?.noticeParty?.faxNumber && (
                        <Col xs={24} md={12}>
                          <div className="title">Fax number:</div>
                          <div>
                            {contractInformation?.noticeParty?.faxNumber}
                          </div>
                        </Col>
                      )}

                      {!!contractInformation?.noticeParty?.noticeMethod && (
                        <Col xs={24} md={12}>
                          <div className="title">Notice method:</div>
                          <div>
                            {contractInformation?.noticeParty?.noticeMethod}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </>
              )}
            </>
          </BasePageCard>
        </Col>
      )}

      {isAdminRole && !isAdditionalInfo2Visible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="Financial Information"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <div className="content-wrapper">
              <BaseSectionMessage
                type="info"
                title="Info"
                message={
                  <span>
                    Please fill in the customer additional information.
                  </span>
                }
              />
            </div>
          </BasePageCard>
        </Col>
      )}

      {isAdditionalInfo2Visible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="Financial Information"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <>
              {isFinancialOverviewVisible && (
                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    <Col xs={24}>
                      <div className="subtitle">Financial overview</div>
                    </Col>

                    {!!financialInformation?.financialOverview
                      ?.creditCheckScore && (
                      <Col xs={24} md={12} data-id="customerCreditCheck">
                        <div className="title">Credit check score:</div>
                        <div>
                          {
                            financialInformation?.financialOverview
                              ?.creditCheckScore
                          }
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview?.fundingType && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerCommissionTypeApproval"
                      >
                        <div className="title">Funding type:</div>
                        <div>
                          {getCommissionTypeApprovalLabel(
                            financialInformation?.financialOverview
                              ?.fundingType,
                          )}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview
                      ?.financialSpecialInstructions && (
                      <Col xs={24} md={12} data-id="customerAddedNotes">
                        <div className="title">Financial special instructions:</div>
                        <div>
                          {
                            financialInformation?.financialOverview
                              ?.financialSpecialInstructions
                          }
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview?.paymentTerm && (
                      <Col xs={24} md={12} data-id="customerPaymentTerms">
                        <div className="title">Payment terms:</div>
                        <div>
                          {getCustomerPaymentTermsLabel(
                            financialInformation?.financialOverview
                              ?.paymentTerm,
                          )}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      financialInformation?.financialOverview
                        ?.customerCostingMethodType,
                    ) && (
                      <Col xs={24} md={12}>
                        <div className="title">
                          Customer costing method type:
                        </div>
                        <div>
                          {getCustomerCostingMethodTypeLabel(
                            financialInformation?.financialOverview
                              ?.customerCostingMethodType,
                          )}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      financialInformation?.financialOverview
                        ?.contractorConversionFees,
                    ) && (
                      <Col xs={24} md={12}>
                        <div className="title">Contractor conversion fees?:</div>
                        <div>
                          {financialInformation?.financialOverview
                            ?.contractorConversionFees
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {isAdminRole &&
                      !!financialInformation?.financialOverview
                        ?.customerCreditNotes && (
                        <Col xs={24} md={12}>
                          <div className="title">Customer credit notes:</div>
                          <div>
                            {
                              financialInformation?.financialOverview
                                ?.customerCreditNotes
                            }
                          </div>
                        </Col>
                      )}

                    {isAdminRole &&
                      !!financialInformation?.financialOverview?.dunsNumber && (
                        <Col xs={24} md={12}>
                          <div className="title">D-U-N-S number:</div>
                          <div>
                            {
                              financialInformation?.financialOverview
                                ?.dunsNumber
                            }
                          </div>
                        </Col>
                      )}

                    {!!financialInformation?.financialOverview?.invoicingCycle && (
                      <Col xs={24} md={12} data-id="customerInvoicingCycle">
                        <div className="title">Invoicing cycle:</div>
                        <div>
                          {getCustomerInvoicingCycleLabel(
                            financialInformation?.financialOverview
                              ?.invoicingCycle,
                          )}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview
                      ?.invoicingInformation && (
                      <Col xs={24} md={12} data-id="customerInvoicingInfo">
                        <div className="title">Invoicing information:</div>
                        <div>
                          {
                            financialInformation?.financialOverview
                              ?.invoicingInformation
                          }
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview
                      ?.specialInvoicingInstructions && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerSpecialInvoicingInstructions"
                      >
                        <div className="title">
                          Special invoicing instructions:
                        </div>
                        <div>
                          {
                            financialInformation?.financialOverview
                              ?.specialInvoicingInstructions
                          }
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview?.payrollCycle && (
                      <Col xs={24} md={12} data-id="customerPayrollCycle">
                        <div className="title">Pay period:</div>
                        <div>
                          {getCustomerPayrollCycleLabel(
                            financialInformation?.financialOverview?.payrollCycle,
                          )}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      financialInformation?.financialOverview?.purchaseOrder,
                    ) && (
                      <Col xs={24} md={12} data-id="customerPurchaseOrder">
                        <div className="title">Purchase order:</div>
                        <div>
                          {financialInformation?.financialOverview?.purchaseOrder
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview
                      ?.purchaseOrderInstructions && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerPurchaseOrderInstructions"
                      >
                        <div className="title">
                          Purchase order instructions:
                        </div>
                        <div>
                          {
                            financialInformation?.financialOverview
                              ?.purchaseOrderInstructions
                          }
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview
                      ?.purchaseOrderFileKey && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerPurchaseOrderFileKey"
                      >
                        <div className="title">Purchase order:</div>
                        <div>
                          <span
                            className="link-imitation"
                            onClick={handleDownloadPurchaseOrderFile}
                          >
                            Customer {customerKey} Purchase order
                          </span>
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview
                      ?.invoiceCutOffPeriod && (
                      <Col xs={24} md={12}>
                        <div className="title">Invoice cut off period:</div>
                        <div>
                          {
                            financialInformation?.financialOverview
                              ?.invoiceCutOffPeriod
                          }
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview
                      ?.invoiceDisputePeriod && (
                      <Col xs={24} md={12}>
                        <div className="title">Invoice dispute period:</div>
                        <div>
                          {
                            financialInformation?.financialOverview
                              ?.invoiceDisputePeriod
                          }
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview?.paymentMethod && (
                      <Col xs={24} md={12}>
                        <div className="title">Payment method:</div>
                        <div>
                          {getPaymentMethodTypeLabel(
                            financialInformation?.financialOverview
                              ?.paymentMethod,
                          )}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview?.payorName && (
                      <Col xs={24} md={12}>
                        <div className="title">Payor name:</div>
                        <div>
                          {
                            financialInformation?.financialOverview?.payorName
                          }
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}

              {isRateOverviewVisible && (
                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    <Col xs={24}>
                      <div className="subtitle">Rate overview</div>
                    </Col>

                    {!isNil(
                      financialInformation?.rateOverview?.dtBillRateMultiplier,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerDtBillRateMultiplier"
                      >
                        <div className="title">DT bill rate multiplier:</div>
                        <div>
                          {financialInformation?.rateOverview
                            ?.dtBillRateMultiplier}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.rateOverview?.fixedMarkupRate && (
                      <Col xs={24} md={12} data-id="customerFixedMarkupRate">
                        <div className="title">Fixed markup rate:</div>
                        <div>
                          {financialInformation?.rateOverview?.fixedMarkupRate}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.rateOverview?.otRateMultiplier && (
                      <Col xs={24} md={12} data-id="customerOtRateMultiplier">
                        <div className="title">OT Rate multiplier:</div>
                        <div>
                          {financialInformation?.rateOverview?.otRateMultiplier}
                        </div>
                      </Col>
                    )}

                  </Row>
                </div>
              )}

              {isMspOrVmsOverviewVisible && (
                <>
                  <Divider className="no-margin" />

                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      <Col xs={24}>
                        <div className="subtitle">MSP/VMS overview</div>
                      </Col>

                      {!isNil(
                        financialInformation?.mspOrVmsOverview?.mspFee,
                      ) && (
                        <Col xs={24} md={12} data-id="customerMspFee">
                          <div className="title">MSP fee:</div>
                          <div>
                            {financialInformation?.mspOrVmsOverview?.mspFee.toFixed(
                              2,
                            )}
                            %
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        financialInformation?.mspOrVmsOverview?.vmsFee,
                      ) && (
                        <Col xs={24} md={12} data-id="customerVmsFee">
                          <div className="title">VMS fee:</div>
                          <div>
                            {financialInformation?.mspOrVmsOverview?.vmsFee.toFixed(
                              2,
                            )}
                            %
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        financialInformation?.mspOrVmsOverview?.isThereMspOrVms,
                      ) && (
                        <Col xs={24} md={12}>
                          <div>
                            <div className="title">Is there an MSP or VMS?:</div>
                            {financialInformation?.mspOrVmsOverview
                              ?.isThereMspOrVms
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}
                      {financialInformation?.mspOrVmsOverview
                        ?.isThereMspOrVms && (
                        <>
                          {!isNil(
                            financialInformation?.mspOrVmsOverview?.url,
                          ) && (
                            <Col xs={24} md={12} data-id="customerUrl">
                              <div className="title">URL:</div>
                              <a
                                href={getWebsiteLink(
                                  financialInformation?.mspOrVmsOverview?.url,
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {getWebsiteName(
                                  financialInformation?.mspOrVmsOverview?.url,
                                )}
                              </a>
                            </Col>
                          )}

                          {!isNil(
                            financialInformation?.mspOrVmsOverview
                              ?.mspOrVmsProviderName,
                          ) && (
                            <Col xs={24} md={12} data-id="customerProviderName">
                              <div className="title">
                                MSP/VMS provider name:
                              </div>
                              <div>
                                {
                                  financialInformation?.mspOrVmsOverview
                                    ?.mspOrVmsProviderName
                                }
                              </div>
                            </Col>
                          )}
                        </>
                      )}
                    </Row>
                  </div>
                </>
              )}
            </>
          </BasePageCard>
        </Col>
      )}

      {isAdminRole && !isAssignmentInfoVisible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="Assignment Information"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <div className="content-wrapper">
              <BaseSectionMessage
                type="info"
                title="Info"
                message={
                  <span>
                    Please fill in the customer additional information.
                  </span>
                }
              />
            </div>
          </BasePageCard>
        </Col>
      )}

      {isAssignmentInfoVisible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="Assignment Information"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <>
              {isAssignmentRequirementsVisible && (
                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    <Col xs={24}>
                      <div className="subtitle">Assignment requirements</div>
                    </Col>

                    {!isNil(
                      assignmentInformation?.assignmentRequirements
                        ?.isIndependentContractorsOrSubcontractors,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerIndependentContractorsPermitted"
                      >
                        <div className="title">
                          Are independent contractors or subcontractors permitted?:
                        </div>
                        <div>
                          {assignmentInformation?.assignmentRequirements
                            ?.isIndependentContractorsOrSubcontractors
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      assignmentInformation?.assignmentRequirements
                        ?.designatedPreScreeningProvider,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerDesignatedPreScreeningProvider"
                      >
                        <div className="title">
                          Designated pre-screening provider:
                        </div>
                        <div>
                          {assignmentInformation?.assignmentRequirements
                            ?.designatedPreScreeningProvider
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      assignmentInformation?.assignmentRequirements
                        ?.isPreScreeningRequired,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerPreScreeningRequired"
                      >
                        <div className="title">Pre-screening required:</div>
                        <div>
                          {assignmentInformation?.assignmentRequirements
                            ?.isPreScreeningRequired
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      assignmentInformation?.assignmentRequirements
                        ?.preScreeningBilledTo,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerPreScreeningBilledTo"
                      >
                        <div className="title">Pre-screening billed to:</div>
                        <div>
                          {getCustomerPreScreeningBilledLabel(
                            assignmentInformation?.assignmentRequirements
                              ?.preScreeningBilledTo,
                          )}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      assignmentInformation?.assignmentRequirements
                        ?.contractBackgroundScreenRequirements,
                    ) && (
                      <Col xs={24} md={12} data-id="customerPreScreening">
                        <div className="title">
                          Contract background screen requirements:
                        </div>
                        <div>
                          {
                            assignmentInformation?.assignmentRequirements
                              ?.contractBackgroundScreenRequirements
                          }
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      assignmentInformation?.assignmentRequirements
                        ?.isOnboardingDocumentationRequired,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerOnboardingDocumentationRequired"
                      >
                        <div className="title">
                          Onboarding documentation required:
                        </div>
                        <div>
                          {assignmentInformation?.assignmentRequirements
                            ?.isOnboardingDocumentationRequired
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!!assignmentInformation?.assignmentRequirements
                      ?.specialInstructions && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerSpecialInstructions"
                      >
                        <div className="title">Special instructions:</div>
                        <div>
                          {
                            assignmentInformation?.assignmentRequirements
                              .specialInstructions
                          }
                        </div>
                      </Col>
                    )}

                    {!!onboardingDocumentationFileKeys?.length && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerOnboardingDocumentation"
                      >
                        <div className="title">Onboarding documentation:</div>
                        <div>
                          {onboardingDocumentationFileKeys
                            .map<React.ReactNode>((key: string, index: number) => (
                              <span
                                key={index}
                                className="link-imitation"
                                onClick={() =>
                                  handleDownloadOnboardingDocumentationFile(key)
                                }
                              >
                                Customer {customerKey} onboarding document{' '}
                                {index + 1}
                              </span>
                            ))
                            .reduce(
                              (
                                previous: React.ReactNode,
                                current: React.ReactNode,
                              ) => [previous, ', ', current],
                            )}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      assignmentInformation?.assignmentRequirements
                        ?.isDrugScreensRequired,
                    ) && (
                      <Col xs={24} md={12}>
                        <div className="title">Drug screens required?:</div>
                        <div>
                          {assignmentInformation?.assignmentRequirements
                            ?.isDrugScreensRequired
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!!assignmentInformation?.assignmentRequirements
                      ?.drugScreenRequirements && (
                      <Col xs={24} md={12}>
                        <div className="title">Drug screen requirements:</div>
                        <div>
                          {
                            assignmentInformation?.assignmentRequirements
                              ?.drugScreenRequirements
                          }
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      assignmentInformation?.assignmentRequirements
                        ?.isCredentialingRequired,
                    ) && (
                      <Col xs={24} md={12}>
                        <div className="title">Is credentialing required?:</div>
                        <div>
                          {assignmentInformation?.assignmentRequirements
                            ?.isCredentialingRequired
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!!assignmentInformation?.assignmentRequirements
                      ?.credentialingRequirements && (
                      <Col xs={24} md={12}>
                        <div className="title">Credentialing requirements:</div>
                        <div>
                          {
                            assignmentInformation?.assignmentRequirements
                              ?.credentialingRequirements
                          }
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      assignmentInformation?.assignmentRequirements
                        ?.isSpecialRatesDefined,
                    ) && (
                      <Col xs={24} md={12}>
                        <div className="title">Special rates defined?:</div>
                        <div>
                          {assignmentInformation?.assignmentRequirements
                            ?.isSpecialRatesDefined
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!!assignmentInformation?.assignmentRequirements
                      ?.specialRateRequirements && (
                      <Col xs={24} md={12}>
                        <div className="title">Special rate requirements:</div>
                        <div>
                          {
                            assignmentInformation?.assignmentRequirements
                              ?.specialRateRequirements
                          }
                        </div>
                      </Col>
                    )}

                    {isAdminRole &&
                      !!assignmentInformation?.assignmentRequirements
                        ?.cspNotes && (
                        <Col xs={24} md={12}>
                          <div className="title">CSP notes:</div>
                          <div>
                            <a
                              href={getWebsiteLink(
                                assignmentInformation?.assignmentRequirements
                                  ?.cspNotes || '',
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {getWebsiteName(
                                assignmentInformation?.assignmentRequirements
                                  ?.cspNotes || '',
                              )}
                            </a>
                          </div>
                        </Col>
                      )}

                  </Row>
                </div>
              )}

              {isSpecialRequirementsVisible && (
                <>
                  <Divider className="no-margin" />

                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      <Col xs={24}>
                        <div className="subtitle">
                          Special assignment requirements
                        </div>
                      </Col>

                      {!!assignmentInformation?.specialAssignmentRequirements
                        ?.guaranteedWorkPerformance && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerGuaranteedWorkPerformance"
                        >
                          <div className="title">
                            Guaranteed work performance:
                          </div>
                          <div>
                            {
                              assignmentInformation?.specialAssignmentRequirements
                                .guaranteedWorkPerformance
                            }
                          </div>
                        </Col>
                      )}

                      {!!assignmentInformation?.specialAssignmentRequirements
                        ?.timeEntry && (
                        <Col xs={24} md={12} data-id="customerTimeEntry">
                          <div className="title">Time entry method:</div>
                          <div>
                            {
                              assignmentInformation?.specialAssignmentRequirements
                                .timeEntry
                            }
                          </div>
                        </Col>
                      )}

                      {!!assignmentInformation?.specialAssignmentRequirements
                        ?.timecardSpecialInstructions && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerAssignmentAddedNotes"
                        >
                          <div className="title">
                            Timecard special instructions:
                          </div>
                          <div>
                            {
                              assignmentInformation?.specialAssignmentRequirements
                                .timecardSpecialInstructions
                            }
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        assignmentInformation?.specialAssignmentRequirements
                          ?.breakInService,
                      ) && (
                        <Col xs={24} md={12} data-id="customerBreakInService">
                          <div className="title">Break in service:</div>
                          <div>
                            {getCustomerBreakInServiceLabel(
                              assignmentInformation?.specialAssignmentRequirements
                                ?.breakInService as BreakInServiceEnum,
                            )}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </>
              )}
            </>
          </BasePageCard>
        </Col>
      )}
    </Row>
  );
}
